import PageLayout from '@/components/Layouts/PageLayout';
import AppBar from '@/shared/AppBar';
import Container from '@/shared/Container';
import { MapType } from '@/types';
import classNames from 'classnames';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import ProfileInfo from '@/components/ProfileManagement/ProfileInfo';
import ProfileUpdateForm from '@/components/ProfileManagement/ProfileUpdateForm';
import { useRouter } from 'next/router';
import { getProfileV2 } from '@/actions/ecom/profileV2';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import useOnMount from '@/hooks/useOnMount';

import {
  FORMTYPES,
  HEADING_LABELS,
  PROFILE_INFORMATION_UPDATE_MESSAGE,
  SHOW_PROFILE_TOAST,
} from '@/constants/profileManagement';
import useToast from '@/hooks/useToast';
import ContentLoader from 'react-content-loader';

const ProfileLoader = (props: any) => (
  <>
    <ContentLoader
      speed={2}
      width={1200}
      height={460}
      viewBox="0 0 1200 460"
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
      {...props}
    >
      <circle cx="583" cy="531" r="15" />
      <rect x="483" y="498" rx="2" ry="2" width="140" height="10" />
      <rect x="488" y="526" rx="2" ry="2" width="140" height="10" />
      <rect x="859" y="794" rx="2" ry="2" width="400" height="400" />
      <circle cx="609" cy="557" r="66" />
      <rect x="8" y="28" rx="0" ry="0" width="308" height="73" />
      <rect x="9" y="128" rx="0" ry="0" width="343" height="246" />
      <rect x="571" y="502" rx="0" ry="0" width="52" height="74" />
      <rect x="543" y="495" rx="0" ry="0" width="82" height="107" />
    </ContentLoader>
  </>
);

const PROFILE_FORM_FIELDS: any = {
  editSecondaryUser: [
    {
      name: 'mobileNumber',
      id: 'mobileNumber',
      label: 'Mobile number*',
      type: 'number',
      value: '',
      rules: {
        maxLength: 10,
        required: true,
      },
    },
    {
      name: 'name',
      id: 'name',
      disabled: false,
      label: 'Name*',
      type: 'text',
      value: '',
      rules: {
        maxLength: 50,
        required: true,
      },
    },
  ],
  editPrimaryUser: [
    {
      name: 'firstName',
      id: 'name',
      disabled: false,
      label: 'First Name*',
      type: 'text',
      value: '',
      rules: {
        maxLength: 50,
        required: true,
      },
    },
    {
      name: 'lastName',
      id: 'name',
      disabled: false,
      label: 'Last Name (Optional)',
      type: 'text',
      value: '',
      rules: {
        maxLength: 50,
        required: false,
      },
    },
  ],

  editEmail: [
    {
      name: 'email',
      id: 'email',
      disabled: false,
      label: 'Email*',
      type: 'text',
      value: '',
      rules: {
        maxLength: 50,
        required: true,
      },
    },
  ],
};

const COMP_PROPS_MAP: MapType = {
  mainProfile: {},
  profileUpdateForm: {
    formFieldList: PROFILE_FORM_FIELDS.primaryUser,
    formType: FORMTYPES.EDIT_PRIMARY_USER,
    headingLabel: HEADING_LABELS.EDIT_PRIMARY_USER,
    contactId: '',
  },
};

const COMP_MAP: MapType = {
  mainProfile: ProfileInfo,
  profileUpdateForm: ProfileUpdateForm,
};

function ProfilePage() {
  const [loader, setLoader] = useState(true);
  const [component, setComponent] = useState('mainProfile');
  const router = useRouter();
  const { triggerDefault } = useToast();

  const handleEditClick = (formType: string, headingLabel: string, contactId?: string) => {
    const updatedProfileUpdateForm = {
      formFieldList: PROFILE_FORM_FIELDS[formType],
      formType,
      headingLabel,
      contactId,
    };
    COMP_PROPS_MAP.profileUpdateForm = updatedProfileUpdateForm;
    setComponent('profileUpdateForm');
  };

  const fetch = (...args: any) => getProfileV2.apply(null, args);
  const { promisifyTrigger, isError, data: profileData, error } = useTriggerRequest({ fetch });

  useOnMount(async () => {
    const showToast = sessionStorage.getItem(SHOW_PROFILE_TOAST);
    if (showToast) {
      triggerDefault({
        message: PROFILE_INFORMATION_UPDATE_MESSAGE,
        durationInms: 9000,
      });
      sessionStorage.removeItem(SHOW_PROFILE_TOAST);
    }
    await promisifyTrigger();

    setLoader(false);
  });

  useEffect(() => {
    if (isError && error?.error) {
      triggerDefault({
        message: error?.error,
        durationInms: 5000,
      });
    }
  }, [error?.error, isError, triggerDefault]);

  const backButtonHandler = useCallback(() => {
    if (component !== 'mainProfile') {
      setComponent('mainProfile');
      return;
    } else {
      router.back();
    }
  }, [router, setComponent, component]);

  const Comp = COMP_MAP[component] ?? Fragment;
  const compProps = COMP_PROPS_MAP[component] ?? {};

  return (
    <PageLayout hideFooter mainClassName={classNames('font-primary', {})}>
      <AppBar onClick={backButtonHandler} />
      <Container dataTestId="profile-page" className="md:max-w-md md:mt-s8  ">
        {loader ? (
          <ProfileLoader />
        ) : (
          <Comp {...compProps} handleEditClick={handleEditClick} profileData={profileData?.data} />
        )}
      </Container>
    </PageLayout>
  );
}

export default ProfilePage;
